import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import type { ContainerVanChecklistData } from '../types/containerVan';

export interface ChartDataPoint {
  hour: string;
  date: string;
  timestamp: Date;
  temperature: number;
  fuel: number;
  remaining_hours: number;
}

export interface ReportStats {
  temperature: { highest: number; lowest: number };
  fuel: { highest: number; lowest: number };
  remaining_hours: { highest: number; lowest: number };
}

/**
 * Extract chart data from checklist record
 */
export const extractChartDataFromChecklist = (checklist: ContainerVanChecklistData): ChartDataPoint | null => {
  try {
    // Convert timestamp to Date
    let timestamp: Date;
    if (checklist.created_at && typeof checklist.created_at === 'object' && 'seconds' in checklist.created_at) {
      timestamp = new Date(checklist.created_at.seconds * 1000);
    } else if (checklist.created_at) {
      timestamp = new Date(checklist.created_at as any);
    } else {
      return null;
    }

    const hour = timestamp.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
    
    const date = timestamp.toLocaleDateString('en-US');

    // Extract values from categories
    let temperature = 0;
    let fuel = 0;

    for (const category of checklist.categories || []) {
      for (const item of category.items || []) {
        // Temperature
        if (item.id === 'temperature_degrees_celsius' && typeof item.value === 'number') {
          temperature = item.value;
        }
        // Fuel
        if (item.id === 'remainin_fuel_liters' && typeof item.value === 'number') {
          fuel = item.value;
        }
      }
    }

    return {
      hour,
      date,
      timestamp,
      temperature,
      fuel,
      remaining_hours: checklist.genSetRemainingHours || 0
    };
  } catch (error) {
    console.error('Error extracting chart data:', error);
    return null;
  }
};

/**
 * Fetch container van checklist data for reporting
 */
export const fetchContainerVanReportData = async (
  vanNumber: string,
  fromDate: Date,
  toDate: Date,
  companyId?: string
): Promise<ChartDataPoint[]> => {
  try {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    // Convert dates to Firestore Timestamps
    const fromTimestamp = Timestamp.fromDate(fromDate);
    const toTimestamp = Timestamp.fromDate(toDate);

    // Build query
    const checklistsRef = collection(db, 'containerVanChecklist');
    const q = query(
      checklistsRef,
      where('companyId', '==', companyId),
      where('vanNumber', '==', vanNumber),
      where('created_at', '>=', fromTimestamp),
      where('created_at', '<=', toTimestamp),
      where('status', '==', 'completed'),
      orderBy('created_at', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const chartData: ChartDataPoint[] = [];

    querySnapshot.forEach((doc) => {
      const checklistData = { id: doc.id, ...doc.data() } as ContainerVanChecklistData;
      const dataPoint = extractChartDataFromChecklist(checklistData);
      
      if (dataPoint) {
        chartData.push(dataPoint);
      }
    });

    return chartData;
  } catch (error) {
    console.error('Error fetching report data:', error);
    throw error;
  }
};

/**
 * Calculate statistics from chart data
 */
export const calculateReportStats = (data: ChartDataPoint[]): ReportStats => {
  if (data.length === 0) {
    return {
      temperature: { highest: 0, lowest: 0 },
      fuel: { highest: 0, lowest: 0 },
      remaining_hours: { highest: 0, lowest: 0 }
    };
  }

  const temperatures = data.map(d => d.temperature);
  const fuels = data.map(d => d.fuel);
  const remainingHours = data.map(d => d.remaining_hours);

  return {
    temperature: {
      highest: Math.max(...temperatures),
      lowest: Math.min(...temperatures)
    },
    fuel: {
      highest: Math.max(...fuels),
      lowest: Math.min(...fuels)
    },
    remaining_hours: {
      highest: Math.max(...remainingHours),
      lowest: Math.min(...remainingHours)
    }
  };
};

/**
 * Format chart data for Chart.js
 */
export const formatChartData = (data: ChartDataPoint[]) => {
  const labels = data.map(d => `${d.date} ${d.hour}`);

  return {
    labels,
    datasets: [
      {
        type: 'line' as const,
        label: 'Temperature (°C)',
        data: data.map(d => d.temperature),
        borderColor: 'rgb(59, 130, 246)', // blue
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        yAxisID: 'y',
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        type: 'line' as const,
        label: 'Remaining Hours',
        data: data.map(d => d.remaining_hours),
        borderColor: 'rgb(16, 185, 129)', // green
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        yAxisID: 'y1',
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        type: 'bar' as const,
        label: 'Fuel (%)',
        data: data.map(d => d.fuel),
        backgroundColor: 'rgba(204, 176, 104, 0.7)', // diesel-like color
        borderColor: 'rgb(204, 176, 104)',
        borderWidth: 1,
        yAxisID: 'y',
      }
    ]
  };
};

/**
 * Chart.js configuration for dual axis chart
 */
export const getChartConfig = (vanNumber?: string) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index' as const,
    intersect: false,
  },
  plugins: {
    title: {
      display: !!vanNumber,
      text: vanNumber ? `Container Van #${vanNumber}` : '',
      position: 'top' as const,
      align: 'center' as const,
      font: {
        size: 18,
        weight: 'bold' as const,
      },
      color: 'rgb(75, 85, 99)', // gray-600
      padding: {
        top: 10,
        bottom: 20,
      }
    },
    legend: {
      position: 'top' as const,
      labels: {
        usePointStyle: true,
        padding: 20,
      }
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: 'white',
      bodyColor: 'white',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      borderWidth: 1,
    },
    zoom: {
      zoom: {
        wheel: {
          enabled: true,
        },
        pinch: {
          enabled: true,
        },
        mode: 'x' as const,
      },
      pan: {
        enabled: true,
        mode: 'x' as const,
      },
    },
  },
  scales: {
    x: {
      display: true,
      title: {
        display: true,
        text: 'Date & Time',
        color: 'rgb(75, 85, 99)', // neutral gray color
        font: {
          weight: 'bold' as const,
        }
      },
      ticks: {
        maxRotation: 45,
        minRotation: 45,
      }
    },
    y: {
      type: 'linear' as const,
      display: true,
      position: 'left' as const,
      title: {
        display: true,
        text: ['Temperature (°C)', 'Fuel (%)'],
        color: 'rgb(75, 85, 99)', // neutral gray color
        font: {
          weight: 'bold' as const,
        }
      },
      grid: {
        drawOnChartArea: true,
      },
    },
    y1: {
      type: 'linear' as const,
      display: true,
      position: 'right' as const,
      title: {
        display: true,
        text: 'Remaining Hours',
        color: 'rgb(75, 85, 99)', // neutral gray color
        font: {
          weight: 'bold' as const,
        }
      },
      grid: {
        drawOnChartArea: false,
      },
    },
  },
});